'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function BlogPageEN() {
  const { t } = useLanguage();

  const blogPosts = [
    {
      id: 1,
      title: 'Botox Prices 2026 - How Much, What Does It Depend On?',
      excerpt: 'One of the most curious questions of those thinking about getting botox: "How much are botox prices in 2026?" In this article, we will discuss current prices, application process and effect in detail.',
      image: '/images/terleme-tedavisi.webp',
      date: '2026-01-15',
      readTime: '8 minutes',
      author: 'Op. Dr. Barış Çin',
      slug: 'botox-prices-2026',
      category: 'Aesthetic Treatments'
    }
  ];

  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/en/blog';
  const pageTitle = 'Dr. Barış Çin Blog - Plastic Surgery and Beauty Tips';
  const pageDescription = 'Current information about plastic surgery, aesthetic operations and beauty on Dr. Barış Çin blog page.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Home', href: '/en' },
              { label: 'Blog', href: '/en/blog' },
            ]}
          />
        </div>

        {/* Hero Section */}
        <div className="text-center mb-12 mt-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-center mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600">
            Dr. Barış Çin Blog
          </h1>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            Current information about plastic surgery, aesthetic treatments and beauty, expert opinions and tips
          </p>
          
          {/* Share Buttons */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <span className="text-gray-600 font-medium">Share Page:</span>
            <div className="flex gap-3">
              <button className="p-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl" onClick={() => handleShare('facebook')}>
                <FaFacebookF className="w-5 h-5" />
              </button>
              <button className="p-3 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors shadow-lg hover:shadow-xl" onClick={() => handleShare('twitter')}>
                <FaTwitter className="w-5 h-5" />
              </button>
              <button className="p-3 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors shadow-lg hover:shadow-xl" onClick={() => handleShare('linkedin')}>
                <FaLinkedinIn className="w-5 h-5" />
              </button>
              <button className="p-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors shadow-lg hover:shadow-xl" onClick={() => handleShare('whatsapp')}>
                <FaWhatsapp className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Categories */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          <button className="px-6 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors">
            All Articles
          </button>
          <button className="px-6 py-2 bg-white text-gray-600 rounded-full hover:bg-gray-50 transition-colors border">
            Aesthetic Treatments
          </button>
          <button className="px-6 py-2 bg-white text-gray-600 rounded-full hover:bg-gray-50 transition-colors border">
            Surgical Procedures
          </button>
          <button className="px-6 py-2 bg-white text-gray-600 rounded-full hover:bg-gray-50 transition-colors border">
            Care Tips
          </button>
        </div>

        {/* Blog Posts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogPosts.map((post) => (
            <Link
              key={post.id}
              href={`/en/blog/${post.slug}`}
              className="group"
            >
              <article className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 group-hover:-translate-y-2">
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <Image
                    src={post.image}
                    alt={post.title}
                    fill
                    className="object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                      {post.category}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">
                    {post.title}
                  </h2>
                  
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>

                  {/* Meta Information */}
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center">
                        <FaUser className="w-4 h-4 mr-1" />
                        <span>{post.author}</span>
                      </div>
                      <div className="flex items-center">
                        <FaClock className="w-4 h-4 mr-1" />
                        <span>{post.readTime}</span>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <FaCalendarAlt className="w-4 h-4 mr-1" />
                      <span>{new Date(post.date).toLocaleDateString('en-US')}</span>
                    </div>
                  </div>
                </div>
              </article>
            </Link>
          ))}
        </div>

        {/* Empty State for More Posts */}
        <div className="text-center mt-12">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              More Content Coming Soon!
            </h3>
            <p className="text-gray-600 mb-6">
              Follow us for more informative content about aesthetic surgery and beauty treatments.
            </p>
            <Link
              href="/en"
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
} 